<template>
  <b-dropdown
    v-if="isDeepLVisible"
    split
    :split-class="splitButtonClasses"
    :toggle-class="splitButtonClasses"
    :class="{ 'disabled-split-button': props.disabled }"
    class="split-button"
    :variant="props.variant"
    @click.prevent="onTranslate(TranslationSetType.All)"
    :disabled="props.disabled">
    <template #button-content>
      <Icon icon="DeepL-black" v-if="!props.textOnly" /><span v-if="!props.iconOnly">{{ props.buttonText ?? $t("translation.deepLTranslation") }}</span>
    </template>
    <b-dropdown-item
      link-class="icon-item"
      @click.prevent="onTranslate(TranslationSetType.All)"
      v-if="props.allEntriesTranslateAvailable"
      :disabled="props.allEntriesTranslateDisabled">
      <span class="dd-item-text"> {{ $t("translation.translateAllValues") }}</span>
    </b-dropdown-item>
    <b-dropdown-item
      link-class="icon-item"
      @click.prevent="onTranslate(TranslationSetType.OnlyEmpty)"
      v-if="props.emptyEntriesTranslateAvailable"
      :disabled="props.emptyEntriesTranslateDisabled">
      <span class="dd-item-text">{{ $t("translation.translateOnlyEmptyValues") }}</span>
    </b-dropdown-item>
    <b-dropdown-item
      link-class="icon-item"
      @click.prevent="onTranslate(TranslationSetType.Selected)"
      v-if="props.selectedEntriesTranslateAvailable"
      :disabled="props.selectedEntriesTranslateDisabled">
      <span class="dd-item-text">{{ $t("translation.TranslateOnlySelectedValues") }}</span>
    </b-dropdown-item>
    <b-dropdown-item class="glossary-item-with-divider" :disabled="props.glossaryDisabled" v-if="props.glossaryAvailable" v-b-modal="props.glossaryModalName">
      <span class="dd-item-text"> {{ $t("translation.editGlossary") }} </span>
    </b-dropdown-item>
  </b-dropdown>
  <GlossaryModal :modal-name="props.glossaryModalName"></GlossaryModal>
  <ConfirmationModal ref="confirmModal"></ConfirmationModal>
</template>

<script setup lang="ts">
import GlossaryModal from "@common/components/Glossary/GlossaryModal.vue";
import { TranslationSetType } from "@medapro-types/common/common-enum";
import { RootActions, RootState } from "@medapro-types/common/rootstate";
import { computed, ref } from "vue";
import { useI18n } from "vue-i18n";
import { useActions, useState } from "vuex-composition-helpers";
import ConfirmationModal from "./ConfirmationModal.vue";
import Icon from "./Icon.vue";

//#region General
const { t: $t } = useI18n();
//#endregion

const props = withDefaults(
  defineProps<{
    allEntriesTranslateDisabled?: boolean;
    allEntriesTranslateAvailable?: boolean;
    allEntriesTranslateConfirmationRequired?: boolean;
    emptyEntriesTranslateDisabled?: boolean;
    emptyEntriesTranslateAvailable?: boolean;
    emptyEntriesTranslateConfirmationRequired?: boolean;
    selectedEntriesTranslateDisabled?: boolean;
    selectedEntriesTranslateAvailable?: boolean;
    selectedEntriesTranslateConfirmationRequired?: boolean;
    overallTranslateActionAvailable?: boolean;
    overallTranslateActionSetType?: TranslationSetType;
    glossaryAvailable?: boolean;
    glossaryDisabled?: boolean;
    glossaryModalName?: string;
    disabled?: boolean;
    iconOnly?: boolean;
    textOnly?: boolean;
    buttonClasses?: string;
    buttonText?: string;
    variant?: "primary" | "secondary";
  }>(),
  {
    allEntriesTranslateDisabled: false,
    allEntriesTranslateAvailable: true,
    allEntriesTranslateConfirmationRequired: true,
    emptyEntriesTranslateDisabled: false,
    emptyEntriesTranslateAvailable: true,
    emptyEntriesTranslateConfirmationRequired: true,
    selectedEntriesTranslateAvailable: false,
    selectedEntriesTranslateDisabled: false,
    selectedEntriesTranslateConfirmationRequired: true,
    overallTranslateActionAvailable: true,
    overallTranslateActionSetType: TranslationSetType.All,
    glossaryAvailable: true,
    glossaryDisable: false,
    glossaryModalName: "deepl-button-glossary-modal",
    disabled: false,
    iconOnly: false,
    buttonClasses: "mono",
    variant: "secondary"
  }
);

const emits = defineEmits<{
  (e: "translate", setType: TranslationSetType): void;
  (e: "translateAll"): void;
  (e: "translateEmpty"): void;
  (e: "translateSelected"): void;
}>();

const { user, userSettings } = useState<RootState>(["user", "userSettings"]);
const { saveUserSettings } = useActions<RootActions>(["saveUserSettings"]);

const confirmationModalRef = ref<typeof ConfirmationModal>();

const splitButtonClasses = computed(() => {
  return `${props.buttonClasses} ${props.iconOnly ? "btn-icon" : "btn-with-icon"}`;
});

const isDeepLVisible = computed(() => {
  return user.value.isDeeplAuthorized;
});

const onTranslate = async (setType: TranslationSetType) => {
  if (!(await confirmDeepLLegalWarning())) {
    return;
  }

  if (setType === TranslationSetType.Selected) {
    if (props.selectedEntriesTranslateConfirmationRequired && !confirm($t("translation.AskTranslateSelectedValues"))) {
      return;
    } else {
      emits("translateSelected");
    }
  } else if (setType === TranslationSetType.All) {
    if (props.allEntriesTranslateConfirmationRequired && !confirm($t("translation.askTranslateAllvalues"))) {
      return;
    } else {
      emits("translateAll");
    }
  } else if (setType === TranslationSetType.OnlyEmpty) {
    if (props.emptyEntriesTranslateConfirmationRequired && !confirm($t("translation.askTranslateEmptyValues"))) {
      return;
    } else {
      emits("translateEmpty");
    }
  }

  emits("translate", setType);
};

async function confirmDeepLLegalWarning() {
  if (userSettings.value.DeepLLegalInformationAccepted === "1") return true;
  try {
    const confirmation = await confirmationModalRef.value?.getConfirmation($t("LegalInformation"), $t("Activity.Description.DeepLLegalInformationText"));
    await saveUserSettings({ DeepLLegalInformationAccepted: confirmation == true ? "1" : "0" });
    return true;
  } catch (error) {
    return false;
  }
}
</script>
